import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowRight, Sun, Moon, Footprints, MapPin, Hotel, UtensilsCrossed,
  Check, X, Users, CalendarDays, ExternalLink, BedDouble,
} from 'lucide-react';
import SEO from '../SEO';
import EventReveal from './EventReveal';
import EventCountdown from './EventCountdown';
import PartnerStrip from './PartnerStrip';
import EventReservationModal from './EventReservationModal';
import useEventCountdown from './useEventCountdown';
import { trackEventCtaClick } from '../../utils/analytics';
import { fetchEventBySlug, eventPath } from '../../utils/eventData';

const GuestCard = ({ guest }) => {
  const [failed, setFailed] = useState(false);
  const initials = guest.name.split(' ').map((w) => w[0]).slice(0, 2).join('');
  return (
    <a
      href={guest.link}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={`event-guest-${guest.name.split(' ')[0].toLowerCase()}`}
      className="group block bg-white rounded-2xl border border-gray-200 overflow-hidden hover:border-[#3D4A26]/40 hover:shadow-lg transition-all duration-300"
    >
      <div className="relative aspect-[4/5] bg-[#3D4A26]/8 overflow-hidden">
        {failed || !guest.photo ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <span
              className="text-4xl md:text-5xl font-bold text-[#3D4A26]/35"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {initials}
            </span>
          </div>
        ) : (
          <img
            src={guest.photo}
            alt={guest.name}
            onError={() => setFailed(true)}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}
        <span className="absolute top-3 left-3 bg-[#C8D843] text-[#1B1B1B] text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full">
          {guest.tag}
        </span>
      </div>
      <div className="p-4 flex items-start justify-between gap-2">
        <h3 className="text-sm md:text-base font-bold text-[#1B1B1B] leading-snug" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          {guest.name}
        </h3>
        <ExternalLink size={15} className="text-[#3D4A26] flex-shrink-0 mt-0.5 opacity-60 group-hover:opacity-100" />
      </div>
    </a>
  );
};

const EventPageView = ({ event }) => {
  const { status, parts } = useEventCountdown(event.startISO, event.endISO);
  const [modalOpen, setModalOpen] = useState(false);
  const [heroFailed, setHeroFailed] = useState(false);
  const ctaVisible = status !== 'finished';
  const guests = event.guests || [];
  const highlights = event.highlights || [];
  const talkGroups = event.talkGroups || [];
  const talks = event.talks || {};
  const talkTopics = talks.topics?.length
    ? talks.topics
    : talkGroups.flatMap((g) => g.topics || []);
  const itinerary = event.itinerary || [];
  const included = event.included || [];
  const excluded = event.excluded || [];
  const partners = event.partners || [];
  const seo = event.seo || {};
  const hero = event.hero || {};
  const pageUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}${eventPath(event)}`;

  const openReservation = (source) => {
    trackEventCtaClick(event.id, { source });
    setModalOpen(true);
  };

  const scrollToProgram = () => {
    const el = document.getElementById('program');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const heroSrc = heroFailed ? event.heroImageFallback : event.heroImage;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    startDate: event.startISO,
    endDate: event.endISO,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    description: seo.description,
    image: [`${origin}${event.heroImage}`, event.heroImageFallback],
    url: pageUrl,
    location: {
      '@type': 'Place',
      name: `${event.locationCity}, ${event.locationRegion}`,
      address: {
        '@type': 'PostalAddress',
        addressLocality: event.locationCity,
        addressRegion: event.locationRegion,
        addressCountry: 'TR',
      },
    },
    organizer: {
      '@type': 'TravelAgency',
      name: 'Camption',
      url: origin,
    },
    performer: guests.map((g) => ({ '@type': 'Person', name: g.name })),
    maximumAttendeeCapacity: event.capacity,
    offers: {
      '@type': 'Offer',
      price: event.price,
      priceCurrency: 'TRY',
      availability: 'https://schema.org/InStock',
      url: pageUrl,
      validFrom: new Date().toISOString().slice(0, 10),
    },
  };

  return (
    <div className="bg-white" data-testid="likya-event-page">
      <SEO
        title={(seo.title || event.name || '').replace(' | Camption', '')}
        description={seo.description}
        image={event.heroImageFallback}
        type="article"
        jsonLd={jsonLd}
      />

      {/* ─── 1. HERO ─── */}
      <section className="relative min-h-[88vh] md:min-h-[92vh] flex items-end overflow-hidden" data-testid="event-hero">
        <img
          src={heroSrc}
          alt={event.heroImageAlt}
          onError={() => setHeroFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(27,27,27,0.82) 0%, rgba(27,27,27,0.35) 38%, rgba(27,27,27,0.55) 68%, rgba(45,58,26,0.94) 100%)',
          }}
        />

        {/* Partner lockup */}
        <div className="absolute top-0 left-0 right-0 z-10 pt-6 md:pt-8">
          <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
            <PartnerStrip partners={partners} onDark size="md" />
          </div>
        </div>

        <div className="relative z-10 w-full max-w-6xl mx-auto px-5 sm:px-6 lg:px-8 pb-12 md:pb-20 pt-28">
          <EventReveal>
            <p className="inline-flex items-center gap-2 text-[10px] md:text-xs uppercase tracking-[0.22em] font-bold text-[#C8D843] bg-[#C8D843]/10 border border-[#C8D843]/30 rounded-full px-3 py-1.5 mb-5">
              {hero.eyebrow}
            </p>
          </EventReveal>

          <EventReveal delay={80}>
            <h1
              className="text-[2rem] leading-[1.08] sm:text-5xl lg:text-6xl font-bold text-white max-w-3xl mb-5"
              style={{ fontFamily: 'DM Sans, sans-serif', letterSpacing: '-0.02em' }}
              data-testid="event-hero-title"
            >
              {hero.title}
            </h1>
          </EventReveal>

          <EventReveal delay={160}>
            <p className="text-white/85 text-sm md:text-lg leading-relaxed max-w-2xl mb-7">
              {hero.subtitle}
            </p>
          </EventReveal>

          <EventReveal delay={200}>
            <div className="flex items-center gap-3 mb-7" data-testid="event-hero-speakers">
              <div className="flex items-center">
                {guests.map((g, i) => (
                  <img
                    key={g.name}
                    src={g.avatar}
                    alt={g.name}
                    loading="lazy"
                    className={`w-11 h-11 md:w-12 md:h-12 rounded-full object-cover border-2 border-white/80 shadow-md ${i > 0 ? '-ml-3' : ''}`}
                  />
                ))}
              </div>
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#C8D843] leading-none mb-1">
                  49W&apos;den konuşmacılar
                </p>
                <p className="text-xs md:text-sm font-semibold text-white/90 leading-snug">
                  {guests.map((g) => g.name).join(' · ')}
                </p>
              </div>
            </div>
          </EventReveal>

          <EventReveal delay={240}>
            <div className="mb-7">
              <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-white/55 mb-2.5">
                {status === 'upcoming' ? 'Başlamasına kalan süre' : 'Durum'}
              </p>
              <EventCountdown status={status} parts={parts} onDark />
            </div>
          </EventReveal>

          {ctaVisible && (
            <EventReveal delay={300}>
              <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                <button
                  type="button"
                  onClick={() => openReservation('hero')}
                  data-testid="event-hero-primary-cta"
                  className="inline-flex items-center justify-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-bold px-7 py-4 rounded-full text-sm md:text-base hover:bg-[#b8c83a] hover:gap-3 transition-all duration-300 shadow-lg shadow-black/20"
                >
                  {hero.primaryCta} <ArrowRight size={17} />
                </button>
                <button
                  type="button"
                  onClick={scrollToProgram}
                  data-testid="event-hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 border border-white/35 text-white font-semibold px-7 py-4 rounded-full text-sm md:text-base backdrop-blur-sm hover:bg-white/10 transition-colors"
                >
                  {hero.secondaryCta}
                </button>
              </div>
            </EventReveal>
          )}
        </div>
      </section>

      {/* ─── 2. HIGHLIGHTS ─── */}
      <section className="py-12 md:py-20 bg-[#FAFAF7]" data-testid="event-highlights">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
            {highlights.map((h, i) => (
              <EventReveal key={h.label} delay={i * 90}>
                <div className="h-full bg-white rounded-2xl border border-gray-200 p-5 md:p-6 hover:border-[#3D4A26]/35 transition-colors">
                  <p
                    className="text-3xl md:text-5xl font-bold text-[#3D4A26] leading-none"
                    style={{ fontFamily: 'DM Sans, sans-serif' }}
                  >
                    {h.value}
                    <span className="text-sm md:text-base font-semibold text-[#1B1B1B]/55 ml-1.5">{h.unit}</span>
                  </p>
                  <p className="text-xs md:text-sm text-gray-600 leading-relaxed mt-2.5">{h.label}</p>
                </div>
              </EventReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 3. GUESTS ─── */}
      <section className="py-12 md:py-20 bg-white" data-testid="event-guests">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <EventReveal>
            <p className="text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-2">
              Konuklar
            </p>
            <h2
              className="text-2xl md:text-4xl font-bold text-[#1B1B1B] leading-tight mb-3"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              49W bu yürüyüşte yanınızda
            </h2>
            <p className="text-gray-600 text-sm md:text-base max-w-xl mb-8">
              Beş gün boyunca aynı patikada yürüyor, akşamları aynı masada buluşuyoruz.
            </p>
          </EventReveal>

          <div className="grid grid-cols-2 gap-4 md:gap-6 max-w-2xl">
            {guests.map((g, i) => (
              <EventReveal key={g.name} delay={i * 120}>
                <GuestCard guest={g} />
              </EventReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 4. EVENING TALKS (dark) ─── */}
      <section className="relative py-14 md:py-24 bg-[#2d3a1a] overflow-hidden" data-testid="event-talks">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              'radial-gradient(circle at 15% 15%, #3D4A26 0, transparent 55%), radial-gradient(circle at 85% 85%, #3D4A26 0, transparent 60%)',
          }}
        />
        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <EventReveal>
            <p className="inline-flex items-center gap-2 text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold text-[#C8D843] mb-3">
              <Moon size={13} /> {talks.eyebrow || 'Akşam Sohbetleri'}
            </p>
            <h2
              className="text-2xl md:text-4xl font-bold text-white leading-tight mb-3"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {talks.title || 'Gün batınca masa kurulur'}
            </h2>
            <p className="text-white/70 text-sm md:text-base max-w-2xl mb-9">
              {talks.subtitle}
            </p>
          </EventReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {talkTopics.map((t, i) => (
              <EventReveal key={t} delay={Math.min(i, 6) * 70}>
                <div
                  data-testid={`event-talk-topic-${i}`}
                  className="h-full flex items-start gap-3.5 bg-white/[0.07] backdrop-blur-sm border border-white/12 rounded-2xl px-4 py-4 md:px-5 md:py-5 transition-colors hover:border-[#C8D843]/40"
                >
                  <span
                    className="flex-shrink-0 w-7 h-7 rounded-full bg-[#C8D843]/15 text-[#C8D843] text-[11px] font-bold flex items-center justify-center tabular-nums"
                    aria-hidden="true"
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="text-white/90 text-sm md:text-[15px] leading-relaxed">{t}</span>
                </div>
              </EventReveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 5. ITINERARY TIMELINE ─── */}
      <section id="program" className="py-14 md:py-24 bg-[#FAFAF7]" data-testid="event-itinerary">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <EventReveal>
            <p className="text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-2">
              Gün Gün Program
            </p>
            <h2
              className="text-2xl md:text-4xl font-bold text-[#1B1B1B] leading-tight mb-3"
              style={{ fontFamily: 'DM Sans, sans-serif' }}
            >
              {event.route}
            </h2>
            <p className="text-gray-600 text-sm md:text-base mb-9">
              {event.dateLabel} · {event.durationLabel} · {event.totalKm} km · Buluşma ve dönüş: {event.meetingPoint}
            </p>
          </EventReveal>

          <div className="relative">
            <span aria-hidden="true" className="absolute left-[18px] md:left-[22px] top-3 bottom-3 w-px bg-[#3D4A26]/18" />
            <div className="space-y-4 md:space-y-6">
              {itinerary.map((d, i) => (
                <EventReveal key={d.day} delay={i * 80}>
                  <div className="relative pl-12 md:pl-16" data-testid={`event-day-${d.day}`}>
                    <span className="absolute left-0 top-1 w-9 h-9 md:w-11 md:h-11 rounded-full bg-[#3D4A26] text-white flex items-center justify-center text-sm md:text-base font-bold shadow-sm">
                      {d.day}
                    </span>
                    <div className="bg-white rounded-2xl border border-gray-200 p-5 md:p-6">
                      <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                        <div>
                          <p className="text-xs font-semibold text-[#3D4A26] flex items-center gap-1.5 mb-1">
                            <CalendarDays size={12} /> {d.date}
                          </p>
                          <h3
                            className="text-base md:text-xl font-bold text-[#1B1B1B] leading-snug"
                            style={{ fontFamily: 'DM Sans, sans-serif' }}
                          >
                            {d.title}
                          </h3>
                        </div>
                        <div className="flex items-center gap-1.5 flex-shrink-0">
                          <span
                            title="Gündüz yürüyüş"
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#C8D843]/25 text-[#3D4A26] px-2 py-1 rounded-full"
                          >
                            <Sun size={11} /> Gündüz
                          </span>
                          {d.eveningTalk && (
                            <span
                              title="Akşam sohbeti"
                              data-testid={`event-day-${d.day}-talk`}
                              className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#2d3a1a] text-white px-2 py-1 rounded-full"
                            >
                              <Moon size={11} /> Sohbet
                            </span>
                          )}
                        </div>
                      </div>

                      <ul className="space-y-2 mb-4">
                        {d.activities.map((a) => (
                          <li key={a} className="flex items-start gap-2.5">
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#3D4A26]/45 flex-shrink-0" />
                            <span className="text-sm text-gray-700 leading-relaxed">{a}</span>
                          </li>
                        ))}
                      </ul>

                      <div className="flex flex-wrap gap-x-4 gap-y-2 pt-3.5 border-t border-gray-100 text-[11px] md:text-xs text-gray-600">
                        <span className="inline-flex items-center gap-1.5 font-semibold text-[#1B1B1B]">
                          <Footprints size={13} className="text-[#3D4A26]" /> {d.km} km
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin size={13} className="text-[#3D4A26]" /> {d.difficulty}
                        </span>
                        {d.stay && (
                          <span className="inline-flex items-center gap-1.5">
                            <Hotel size={13} className="text-[#3D4A26]" /> {d.stay}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1.5">
                          <UtensilsCrossed size={13} className="text-[#3D4A26]" /> {d.meals}
                        </span>
                      </div>
                    </div>
                  </div>
                </EventReveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── 6. INCLUDED / EXCLUDED ─── */}
      <section className="py-14 md:py-24 bg-white" data-testid="event-included">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <EventReveal>
              <h2
                className="text-xl md:text-2xl font-bold text-[#1B1B1B] mb-5"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Dahil olanlar
              </h2>
              <ul className="space-y-3">
                {included.map((it) => (
                  <li key={it} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-[#C8D843] flex items-center justify-center flex-shrink-0">
                      <Check size={12} className="text-[#1B1B1B]" strokeWidth={3} />
                    </span>
                    <span className="text-sm md:text-[15px] text-gray-700 leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
            </EventReveal>

            <EventReveal delay={120}>
              <h2
                className="text-xl md:text-2xl font-bold text-[#1B1B1B] mb-5"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Dahil olmayanlar
              </h2>
              <ul className="space-y-3">
                {excluded.map((it) => (
                  <li key={it} className="flex items-start gap-3">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                      <X size={12} className="text-gray-600" strokeWidth={3} />
                    </span>
                    <span className="text-sm md:text-[15px] text-gray-700 leading-relaxed">{it}</span>
                  </li>
                ))}
              </ul>
            </EventReveal>
          </div>

          {(event.singleRoomSupplement || 0) > 0 && (
            <EventReveal delay={200}>
              <div
                className="mt-8 flex items-start gap-3 bg-[#f0f4d8] border border-[#C8D843]/40 rounded-2xl p-4 md:p-5"
                data-testid="event-single-room-info"
              >
                <BedDouble size={18} className="text-[#3D4A26] flex-shrink-0 mt-0.5" />
                <p className="text-sm text-[#3D4A26]/90 leading-relaxed">
                  <strong>Tek kişilik oda notu:</strong> Fiyatlar çift kişilik odada kişi başıdır. Tek kişilik oda
                  tercih edenler için gecelik {new Intl.NumberFormat('tr-TR').format(event.singleRoomSupplement)}₺
                  tek kişi farkı uygulanır
                  {event.nights ? `, ${event.nights} gece için toplam ${new Intl.NumberFormat('tr-TR').format(event.singleRoomSupplement * event.nights)}₺ ek ücret oluşur` : ''}.
                </p>
              </div>
            </EventReveal>
          )}
        </div>
      </section>

      {/* ─── 7. PARTNERS ─── */}
      <section className="py-14 md:py-20 bg-[#FAFAF7] border-t border-gray-200" data-testid="event-partners">
        <div className="max-w-6xl mx-auto px-5 sm:px-6 lg:px-8">
          <EventReveal>
            <p className="text-[11px] md:text-xs uppercase tracking-[0.2em] font-semibold text-[#3D4A26] mb-6">
              Bu Etkinliği Birlikte Düzenliyoruz
            </p>
          </EventReveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {partners.map((p, i) => {
              const external = p.link.startsWith('http');
              const Wrapper = external ? 'a' : Link;
              const props = external
                ? { href: p.link, target: '_blank', rel: 'noopener noreferrer' }
                : { to: p.link };
              return (
                <EventReveal key={p.key} delay={i * 100}>
                  <Wrapper
                    {...props}
                    data-testid={`event-partner-${p.key}`}
                    className="group h-full flex flex-col bg-white rounded-2xl border border-gray-200 p-5 md:p-6 hover:border-[#3D4A26]/35 hover:shadow-sm transition-all"
                  >
                    <div className="h-9 flex items-center mb-4">
                      <PartnerStrip partners={[p]} onDark={false} size="md" testId={`event-partner-logo-${p.key}`} />
                    </div>
                    <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#3D4A26]/70 mb-2">{p.role}</p>
                    <p className="text-sm text-gray-600 leading-relaxed flex-1">{p.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-xs font-bold text-[#3D4A26] mt-4 group-hover:gap-2.5 transition-all">
                      İncele <ArrowRight size={13} />
                    </span>
                  </Wrapper>
                </EventReveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── 8. CLOSING CTA ─── */}
      <section className="py-14 md:py-20 bg-[#3D4A26]" data-testid="event-closing-cta">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-7">
            <div>
              <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C8D843] mb-3">
                <CalendarDays size={13} /> {event.dateLabel} · {event.dayLabel}
              </p>
              <h2
                className="text-2xl md:text-4xl font-bold text-white leading-tight mb-3 max-w-xl"
                style={{ fontFamily: 'DM Sans, sans-serif' }}
              >
                Kontenjan {event.capacity} kişi. Yerinizi şimdi ayırın.
              </h2>
              <p className="text-white/75 text-sm md:text-base flex flex-wrap items-center gap-x-4 gap-y-1">
                <span className="inline-flex items-center gap-1.5"><Users size={14} /> {event.capacity} kişilik tek grup</span>
                <span className="inline-flex items-center gap-1.5"><Footprints size={14} /> {event.totalKm} km</span>
                <span className="font-bold text-[#C8D843]">{event.priceLabel} kişi başı</span>
              </p>
            </div>
            {ctaVisible ? (
              <button
                type="button"
                onClick={() => openReservation('closing')}
                data-testid="event-closing-cta-button"
                className="inline-flex items-center justify-center gap-2 bg-[#C8D843] text-[#1B1B1B] font-bold px-8 py-4 rounded-full text-sm md:text-base hover:bg-[#b8c83a] hover:gap-3 transition-all duration-300 flex-shrink-0"
              >
                {hero.primaryCta} <ArrowRight size={17} />
              </button>
            ) : (
              <EventCountdown status={status} parts={parts} onDark testId="event-closing-countdown" />
            )}
          </div>
        </div>
      </section>

      {/* ─── 9. MOBILE STICKY CTA ─── */}
      {ctaVisible && (
        <div
          className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-gray-200 px-4 py-3 flex items-center gap-3"
          data-testid="event-sticky-cta"
        >
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-gray-500 leading-none">
              {event.dateLabel}
            </p>
            <p className="text-sm font-bold text-[#1B1B1B] leading-tight mt-1">{event.priceLabel}</p>
          </div>
          <button
            type="button"
            onClick={() => openReservation('sticky')}
            data-testid="event-sticky-cta-button"
            className="flex-1 bg-[#C8D843] text-[#1B1B1B] font-bold py-3 rounded-full text-sm hover:bg-[#b8c83a] transition-colors"
          >
            {hero.primaryCta}
          </button>
        </div>
      )}
      {ctaVisible && <div className="h-20 md:hidden" aria-hidden="true" />}

      {modalOpen && <EventReservationModal event={event} onClose={() => setModalOpen(false)} />}
    </div>
  );
};

const EventPage = () => {
  const { slug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchEventBySlug(slug).then((ev) => {
      if (!alive) return;
      setEvent(ev);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [slug]);

  if (loading) {
    return <div className="py-32 text-center text-gray-500" data-testid="event-page-loading">Yükleniyor...</div>;
  }
  if (!event) {
    return (
      <div className="py-32 text-center" data-testid="event-page-notfound">
        <p className="text-gray-600 mb-3">Aradığınız sayfa bulunamadı.</p>
        <Link to="/" className="text-[#3D4A26] font-semibold underline">Ana Sayfaya Dön</Link>
      </div>
    );
  }
  return <EventPageView event={event} />;
};

export default EventPage;
